import { showToast } from '@lokalise/louis';

import { type ProjectSetupResponse } from '@app/api/projectSetup';
import { onboardingStorageKeys } from '@app/features/quick-start/constants';
import { type OnboardingPath } from '@app/features/quick-start/data';

import type { Policy } from './components';
import type { ResponseError } from './types';

export const hasErrorForField = (error: ResponseError | null, field: string) =>
    !!error && !!error.violations && field in error.violations;

type GetErrorForFieldType = (error: ResponseError | null, field: string) => string[] | undefined;

export const getErrorForField: GetErrorForFieldType = (error: ResponseError | null, field) =>
    error?.violations?.[field];

export const isCommonError = (error: Pick<ResponseError, 'violations'> | null): error is ResponseError =>
    !!error && !error.violations;

// add default policy: password can't match email
export const getPolicy = (policy: Policy): Policy => ({ ...policy, matchesEmail: 'true' });

const encodeChars = (input: string) =>
    input.replaceAll(/[._-]/g, (match) => {
        switch (match) {
            case '.':
                return '+';
            case '_':
                return '/';
            case '-':
                return '=';
            default:
                return match;
        }
    });

const getErrorMessageFromUrl = () => {
    if (typeof window === 'undefined') {
        return undefined;
    }

    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');

    if (error && typeof error === 'string') {
        return atob(encodeChars(error));
    }

    return undefined;
};

const removeErrorMessageFromUrl = () => {
    if (typeof window === 'undefined') {
        return;
    }

    const params = new URLSearchParams(window.location.search);
    params.delete('error');

    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.replaceState({}, '', newUrl);
};

/*
 * In case of an error during social login/signup, redirect user. The URL will reflect the error.
 * The backend will attach an encoded message specific to this error, that can be utilized for display on frontend.
 * @Note This process applies only when redirection is to either signup or login page.
 * */
export const handleErrorInUrl = () => {
    const errorMessage = getErrorMessageFromUrl();

    if (!errorMessage) {
        return;
    }

    showToast({
        type: 'error',
        title: errorMessage,
    });

    removeErrorMessageFromUrl();
};

/*
 * Returns `true` if project was created and a redirect is performed.
 */
export const handleProjectSetupResponseData = (data: ProjectSetupResponse): boolean => {
    const projectId = data.projectId ?? '';

    if (projectId === '') {
        // No need to handle this case
        return false;
    }

    const onboardingPath: OnboardingPath = data.onboardingPath ?? '';
    localStorage.setItem(onboardingStorageKeys.modal, 'true');

    if (onboardingPath === 'upload' || onboardingPath === 'ad_hoc_document_upload') {
        window.location.assign(`/upload/${projectId}/`);
    } else if (onboardingPath === 'git') {
        window.location.assign(`/apps/${projectId}/`);
    } else {
        window.location.assign(`/project/${projectId}/`);
    }

    return true;
};
